import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
  LinearProgress,
  Fade,
  keyframes
} from '@mui/material';

// Pulse animation for enhanced loading effect
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

interface ModernLoaderProps {
  size?: number;
  message?: string;
  variant?: 'simple' | 'with-background' | 'minimal' | 'fullscreen' | 'inline' | 'button';
  color?: 'primary' | 'secondary' | 'inherit';
  thickness?: number;
  showProgress?: boolean;
  progress?: number;
  overlay?: boolean;
}

const ModernLoader: React.FC<ModernLoaderProps> = ({
  size = 40,
  message,
  variant = 'simple',
  color = 'primary',
  thickness = 4,
  showProgress = false,
  progress = 0,
  overlay = false
}) => {
  const theme = useTheme();

  const getLoaderStyles = () => {
    const baseStyles = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    switch (variant) {
      case 'fullscreen':
        return {
          ...baseStyles,
          position: 'fixed' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: overlay
            ? 'rgba(248, 250, 252, 0.95)'
            : 'transparent',
          backdropFilter: overlay ? 'blur(6px)' : 'none',
          zIndex: 9999,
          flexDirection: 'column' as const,
          gap: 3,
        };
      case 'with-background':
        return {
          ...baseStyles,
          position: 'absolute' as const,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(4px)',
          zIndex: 10,
          flexDirection: 'column' as const,
          gap: 2,
        };
      case 'inline':
        return {
          ...baseStyles,
          padding: theme.spacing(2),
          flexDirection: 'column' as const,
          gap: 2,
        };
      case 'button':
        return {
          ...baseStyles,
          gap: 1,
        };
      case 'minimal':
        return {
          ...baseStyles,
          padding: theme.spacing(1),
        };
      default: // simple
        return {
          ...baseStyles,
          flexDirection: 'column' as const,
          gap: 2,
          padding: theme.spacing(3),
        };
    }
  };
  return (
    <Fade in={true} timeout={300}>
      <Box sx={getLoaderStyles()}>
        {/* Progress indicators */}
        {showProgress && variant !== 'button' && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress
              variant={progress > 0 ? 'determinate' : 'indeterminate'}
              value={progress}
              color={color}
              sx={{ borderRadius: 2, height: 6 }}
            />
          </Box>
        )}

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Background pulse effect */}
          {variant !== 'minimal' && variant !== 'button' && (
            <Box
              sx={{
                position: 'absolute',
                width: size + 16,
                height: size + 16,
                borderRadius: '50%',
                background: color === 'inherit'
                  ? `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`
                  : `linear-gradient(45deg, ${alpha(theme.palette[color as 'primary' | 'secondary'].main, 0.1)} 0%, ${alpha(theme.palette[color as 'primary' | 'secondary'].light, 0.1)} 100%)`,
                animation: `${pulseAnimation} 2s infinite`,
              }}
            />
          )}

          {/* Main progress circle */}
          <CircularProgress
            size={size}
            thickness={thickness}
            color={color}
            sx={{
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>

        {/* Loading message */}
        {message && variant !== 'minimal' && variant !== 'button' && (
          <Typography
            variant={variant === 'fullscreen' ? 'h6' : 'body2'}
            color="text.secondary"
            sx={{
              fontWeight: 500,
              textAlign: 'center',
              mt: 1,
              animation: `${pulseAnimation} 2s infinite`,
            }}
          >
            {message}
          </Typography>
        )}

        {/* Inline message for button variant */}
        {message && variant === 'button' && (
          <Typography variant="body2" sx={{ ml: 1 }}>
            {message}
          </Typography>
        )}

        {/* Progress percentage */}
        {showProgress && progress > 0 && variant !== 'button' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {Math.round(progress)}%
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

export default ModernLoader;
